import {observer, classNames, React, toJS, computed, useEffect} from '@api'
import {Popover, Spinner} from '@blueprintjs/core'
import {Popover2, Tooltip2} from '@api'
import {CircularProgress, Tooltip} from '@material-ui/core'
import {BuilderCardLoader, GroupByGroupStore, IContextMenuItems, SpecSwatch, useGroupByGroupStore, useVipLocalStore} from '@modules'
import {ICatalogFieldConfig, StoresInstance} from '@stores'
import {action, autorun, observable, runInAction, trace, when} from 'mobx'
import {VipLocalStore} from '@modules'
import {Fragment, useContext} from 'react'
import css from './GroupSwatches.css'
import { GroupSwatchesContextMenu } from './GroupSwatchesContextMenu'

export interface ISwatchHoverItem {
  item: any,
  field: ICatalogFieldConfig,
  image?: any
}

export type GroupSwatchContextMenuExtra = {
  item: any;
  field: ICatalogFieldConfig;
  esIndex: string
}

export class GroupBySwatchesStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance,public groupStore: GroupByGroupStore) {
    super(stores)
    groupStore.swatchStore = this
  }

  @computed get swatchFields() {
    const {groupStore: {props: {level}, grouping}, catalog: {catalogFieldsForLibrary, _fieldByName, groupBy: {hierarchy}}} = this,
      path = hierarchy.slice(0, level + 1)

    let r = _.flatMap((grouping.settings.swatches ?? []).map(f => f && (_fieldByName[_.isString(f) ? f : f.field] ?? catalogFieldsForLibrary(f.field)))).filter(_.identity)
            // : specFields.filter(f => !_.includes(path, f.esField ?? f.field))
    r = _.uniqBy(r.filter(_.identity), v => v.esField ?? v.field)

    return r
  }

  @observable loading = false

  registerEffects = () => {
    let firstRender = true;

    const toDispose = [
      autorun(async () => {
        if (!this.groupStore.grouping) return
        const {swatchFields, groupStore: {childGroupStore: {productQueryVariables}}, catalog: {es: {refetchId}}}= this

        // Query distinct spec ids for the selected catalog items
        if (!_.isEmpty(swatchFields)) {
          runInAction(async () => {
            if (!_.isEmpty(this.swatchData) && firstRender) {
              firstRender = false
            }
            else {
              this._loadSwatchData()
            }
          })
        }
      }, {
        name: `Query for swatch data`
      })
    ]

    return () => toDispose.forEach(f => f())
  }

  get swatchData() {
    return this.groupStore.swatchData
  }

  /**
   * TODO - needs to be cancellable as we can change filters rapidly and do this
   */
  _loadSwatchDataDebounced = _.debounce(() => this._loadSwatchData(), 50)

  @action _loadSwatchData = async () => {
    if (!this.groupStore?.grouping) return
    if (this.loading) {
      when(() => !this.loading, () => this._loadSwatchDataDebounced(), {name: `Load swatch data (debounced)`})
    }

    const {es, catalog, swatchFields, groupStore: {pathLookup, childGroupStore: {productQueryVariables}}} = this

    if (!productQueryVariables) return

    //console.log(toJS({productQueryVariables, swatchFields}, {recurseEverything: true}))
    this.loading = true
    const {aggregations} = await es.search({
      meta: `Swatch data load for ${pathLookup}`,
      body: {
        size: 0,
        query: productQueryVariables.query,
        aggs: swatchFields.reduce((acc, {esField, field}) =>
          ({
            ...acc,
            [field]: {
              terms: {
                field: `${esField ?? field}.exId`,
                size: 100
              }
            }
          }), {})
      }
    }, {log: {query: false}})

    // console.log(aggregations)
    // Get the corresponding items from their own indexes
    const msearchBody = swatchFields.reduce((acc, field) => {
      const {buckets} = aggregations[field.field],
        ids = buckets.map(b => b.key)

      return [
        ...acc,
        {index: field.esIndex},
        {query: {terms: {exId: ids}}}
      ]
    }, [])

    const {responses} = await es.msearch({
      body: msearchBody,
      meta: `Load swatch data for ${this.groupStore.groupLabel}`
    }, {log: {query: false, result: false}})

    this.groupStore.swatchData = swatchFields.reduce((acc, field, i) => {
      const r = responses[i],
        hits = r?.hits?.hits ?? [],
        items = hits.map(v => v._source)

      // console.log(toJS({items}, {recurseEverything: true}))

      return {
        ...acc,
        [field.field]: _.sortBy(items, item => item?.name ?? catalog.idFor(item))
      }
    }, {})
    this.loading = false
  }

  get hoverItem() { return this.groupStore.contextualHoverItem as ISwatchHoverItem }
  set hoverItem(item) { this.groupStore.contextualHoverItem = item }

  @action onMouseLeave = e => {
    this.hoverItem = null
  }

  selectedSwatches = observable.map<string, ISwatchHoverItem>({}, {deep: false})

  swatchMouseEventsFor : (item: ISwatchHoverItem) => Partial<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> =item => {
    const {catalog, catalog: {sidebar}} = this
    return ({
      onMouseEnter: e => {
        this.hoverItem = item
      },
      onMouseLeave: e => {
        if (this.hoverItem?.item === item?.item) {
          this.hoverItem = null
        }
      },
      onClick: e => {
        e.stopPropagation() // Otherwise click on group will trigger and clear our selection

        if (e.shiftKey) {
          this.groupStore.selectContextualHoverCatalogItems(e)
        } else {
          const {selectedSwatches} = this,
            id = catalog.idFor(item.item)

          selectedSwatches.has(id) ? selectedSwatches.delete(id) : selectedSwatches.set(id, item)
        }
      },
      onDoubleClick: e => {
        const {field} = item,
          library = field.esIndex ? catalog.libraryByIndex[field.esIndex] : null

        if (library) {
          e.stopPropagation()
          sidebar.left.navigateToLibrary({libraryId: library.id, where: [{value: catalog.idFor(item.item)}]}, true)
        }
      }
    })
  }

  @computed get swatchDataByField() : Array<{catalogField, data}> {
    const {swatchFields, swatchData } = this
    return _.flatMap(swatchFields.map((catalogField) => {
        const {field, esField, esIndex} = catalogField,
          data = swatchData[field]

        return _.isEmpty(data) ? null : data.map((v, i) => ({catalogField, data: v}))
      })).filter(_.identity)
  }

  @computed get content() {
    const {onContextMenu, groupStore: {isExpanded, contextualHoverCatalogItem, selectedCatalogItemsInGroup}, onMouseLeave} = this
    return (<div className={css.swatches}
                 data-type='swatches'
                 data-contextual-hover-item={contextualHoverCatalogItem != null || !_.isEmpty(selectedCatalogItemsInGroup)}
                 onMouseLeave={onMouseLeave}
                 onContextMenu={onContextMenu}
                 data-parent-expanded={isExpanded} >
      <SpecSwatches />
    </div>)
  }

  onContextMenu = (e: React.MouseEvent, extra?: GroupSwatchContextMenuExtra) => {
    this.contextMenu.onComponentContextMenu(e, this, extra)
  }

  get contextMenuItems() {
    return <GroupBySwatchesStoreContext.Provider value={this}>
      <GroupSwatchesContextMenu />
    </GroupBySwatchesStoreContext.Provider>
  }
}

const SpecSwatches = observer(function SpecSwatches() {
  const {swatchDataByField, loading, catalog} = useGroupBySwatchesStore()
  return (
    <Fragment>
      {loading && _.isEmpty(swatchDataByField) ? <CircularProgress className={css.loading}/>
       : swatchDataByField.map(({catalogField, data}, i) => {
        const {field, esField, esIndex} = catalogField

        return (
          <Tooltip2 content={<div className={css.swatchTooltip}><label>{catalog.labelForField(catalogField)}:<br/></label>{data?.name ?? ''}</div>}
                   placement='top' key={i}>
            <SpecSwatch item={data} field={catalogField} key={i} esIndex={esIndex}/>
          </Tooltip2>)
      })
    }

    </Fragment>
  )
});

export const GroupBySwatchesStoreContext = React.createContext<GroupBySwatchesStore>(undefined)
export const useGroupBySwatchesStore = () => useContext(GroupBySwatchesStoreContext)

export const GroupSwatches = observer<{ className?: string }>(function GroupSwatches({className}) {
  const groupStore = useGroupByGroupStore(),
    store = useVipLocalStore((ws) => new GroupBySwatchesStore(ws, groupStore)),
    {catalog, swatchDataByField, content} = store

  useEffect(() => store.registerEffects(), [])

  if (_.isEmpty(swatchDataByField)) { // Don't show a single swatch
    return null
  }

  return (
    <GroupBySwatchesStoreContext.Provider value={store}>
    <Popover2
      className={classNames(css.root, className)}
      interactionKind='hover'
      disabled // Don't show popover, instead double click to open library
      placement='right'
      minimal
      content={<SwatchPopoverContent />}
    >
      {content}
    </Popover2>
    </GroupBySwatchesStoreContext.Provider>)
})

const SwatchPopoverContent = observer(() => {
  const {hoverItem, catalog} = useGroupBySwatchesStore()
  return hoverItem ? <BuilderCardLoader hideButtons isPopover levelOfDetail={1} nested={{field: hoverItem.field, data: hoverItem.item }} id={catalog.idFor(hoverItem.item)} image={hoverItem.image}/> : null
})



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/GroupSwatches.tsx